import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Blank, Panel, Row } from '../components/ui/panel'
import { Chip } from '../components/ui/tone'
import { api, type OutboundStatus } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * The platform plane — architecture.md §47; P027's second plane.
 *
 * **Everything here belongs to Axmion, not to a customer.** No organization role reaches it,
 * however senior: a tenant's SUPER_ADMIN stopping every other tenant's mail is precisely what two
 * planes exist to prevent.
 *
 * ### Why this screen exists
 *
 * `POST /system/platform/outbound` was reachable only by `curl` until 2026-08-22
 * ([P039](../../docs/problems/p039-platform-kill-switch-had-no-endpoint.md) built the endpoint;
 * nothing called it). **The switch that stops every customer at once being API-only is a thing to
 * know during an incident, not to discover during one** — a stop control that needs a terminal, a
 * session cookie and the right JSON body is not a stop control.
 *
 * ### The asymmetry is deliberate, and it matches the organization tier
 *
 * **Stopping is one press.** An emergency stop that demands a confirmation dialog is not an
 * emergency stop. **Resuming asks for a reason**, because that is the direction that lets mail
 * reach real people, and the reason is audited.
 *
 * ### What it will not do
 *
 * No organization list, no impersonation, no per-tenant control. Those are the SaaS surface P027
 * defers on purpose, and drawing them here as dead controls would make this the worst version of
 * the screen rather than the most complete one. The other platform-plane surface is
 * [Webhooks](webhooks.tsx), which is its own entry because it answers a different question.
 */
export function PlatformScreen() {
  const { user, isPlatformStaff } = useSession()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')

  const status = useQuery({
    queryKey: ['outbound'],
    queryFn: () => api.get<OutboundStatus>('/system/outbound'),
    enabled: isPlatformStaff,
  })

  const set = useMutation({
    mutationFn: (input: { enabled: boolean; reason: string }) =>
      api.post('/system/platform/outbound', input),
    onSuccess: () => {
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['outbound'] })
      void queryClient.invalidateQueries({ queryKey: ['health'] })
    },
  })

  if (!isPlatformStaff) {
    return (
      <Screen>
        <ScreenHeader
          title="Platform"
          description="Axmion's own controls, across every organization."
        />
        <Panel>
          <Blank
            icon="denied"
            title="This is the platform plane"
            body="It belongs to Axmion rather than to any organization, and no organization role reaches it — including SUPER_ADMIN. That separation is the point: one customer must never be able to stop another customer's mail."
          />
        </Panel>
      </Screen>
    )
  }

  // The **platform** permission, not an organization one. `can()` answers about the membership and
  // would say no to an Axmion operator who has every right to press this.
  const may = (user?.platformPermissions ?? []).includes('platform_pause_all')
  const sending = status.data?.platform ?? false

  return (
    <Screen>
      <ScreenHeader
        title="Platform"
        description="Axmion's own controls, across every organization. Nothing here belongs to a tenant."
      />
      <Panel
        title="Outbound sending, everywhere"
        description="§47's platform tier. It stops every organization at once, and no tenant can start sending again while it is off."
      >
        <Row label="Platform">
          <div className="flex flex-wrap items-center gap-3">
            <Chip tone={sending ? 'ok' : 'bad'}>{sending ? 'sending' : 'stopped'}</Chip>
            {may &&
              (sending ? (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={set.isPending}
                  onClick={() =>
                    set.mutate({
                      enabled: false,
                      reason: reason || 'stopped from the platform screen',
                    })
                  }
                >
                  Stop every organization
                </Button>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is it safe to resume?"
                    aria-label="Reason for resuming platform-wide sending"
                    className="bg-panel2 border-line focus:border-accent w-72 rounded-[7px] border px-2 py-1 text-[12.5px] transition-colors focus:outline-none"
                  />
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={set.isPending || reason.trim().length < 4}
                    onClick={() => set.mutate({ enabled: true, reason })}
                  >
                    Resume for everyone
                  </Button>
                </div>
              ))}
          </div>
        </Row>

        <Row label="This organization">
          {/* Shown because "why can I not send" has to be answerable from one place, and the
              effective answer is the AND of both tiers. Changing it is the Settings screen's job,
              which is the tier a customer owns. */}
          <div className="flex items-center gap-3">
            <Chip tone={status.data?.organization ? 'ok' : 'bad'}>
              {status.data?.organization ? 'sending' : 'stopped'}
            </Chip>
            <span className="text-faint text-[11.5px]">Its own tier, on Settings.</span>
          </div>
        </Row>

        <Row label="Effective">
          <Chip tone={status.data?.enabled ? 'ok' : 'bad'}>
            {status.data?.enabled ? 'mail can leave' : 'nothing will send'}
          </Chip>
        </Row>

        {!may && (
          <Row label="Your access">
            <span className="text-muted text-[12px]">
              Stopping and resuming needs <code>platform_pause_all</code>, which your platform role
              does not hold.
            </span>
          </Row>
        )}
      </Panel>
    </Screen>
  )
}
